import { styled } from "goober";
import { formatCompactDuration } from "../../lib/formatCompactDuration";
import { formatHoverTime } from "../../lib/formatTimestamp";
import { ClickButton } from "../ClickButton";
import { MetaSep } from "../MetaSeparator";
import { PressButton } from "../PressButton";
import { DefaultIcon, HoverIcon } from "../ToggleIcon";

const headerStyle = `
  display: flex;
  align-items: center;
  height: 28px;
  gap: 4px;
  padding: 6px 10px 6px 8px;
  border-radius: 9999px;
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
  text-align: left;
  &:disabled {
    opacity: 1;
  }
  /* goober requires :hover before :not(:disabled) to avoid a descendant selector. */
  &:hover:not(:disabled) {
    background-color: var(--alpha4);
  }
`;

export const CollapsibleHeader = styled(PressButton)`
  ${headerStyle}
  &:hover:not(:disabled) {
    ${DefaultIcon} {
      opacity: 0;
      transform: rotate(90deg) scale(0.5);
    }
    ${HoverIcon} {
      opacity: 1;
      transform: rotate(0deg) scale(1);
    }
  }
`;

export const ActionHeader = styled(ClickButton)`
  ${headerStyle}
`;

export const CollapsibleHeaderInner = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;

  & > *:not(:first-child) {
    display: block;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ExpandWrapper = styled("div")`
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.12s ease-out;
  &[data-expanded="true"] {
    grid-template-rows: 1fr;
  }
`;

export const ExpandInner = styled("div")`
  overflow: hidden;
  min-height: 0;
  content-visibility: hidden;
  &[data-expanded="true"] {
    content-visibility: visible;
  }
`;

export const ExpandedContent = styled("div")`
  margin-top: 8px;
  margin-bottom: 12px;
  background-color: var(--alpha4);
  padding: 12px;
  border-radius: 12px;
  border: 0.5px solid var(--alpha8);
  overflow: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Timestamp = styled("div")`
  position: absolute;
  bottom: 12px;
  left: 12px;
  font-family: inherit;
  font-size: 9px;
  line-height: 12px;
  font-weight: 450;
  letter-spacing: -1%;
  color: var(--text-primary);
  opacity: 0;
  transform: translateY(-4px);
  transition:
    opacity 80ms ease-out,
    transform 80ms ease-out;
  pointer-events: none;
  white-space: nowrap;
`;

export const ExpandedContentHover = styled("div")`
  position: relative;
  padding-bottom: 28px;
  & > ${ExpandedContent} {
    margin-bottom: 0;
  }
  &:hover ${Timestamp} {
    opacity: 0.3;
    transform: translateY(0);
    transition-delay: 600ms;
  }
`;

export function ExpandedTimestamp({
  createdAt,
  durationMs,
}: {
  createdAt: number;
  durationMs?: number | null;
}) {
  const time = formatHoverTime(createdAt);
  const duration =
    durationMs != null && durationMs > 0
      ? formatCompactDuration(durationMs)
      : null;
  return (
    <Timestamp>
      {time}
      {duration && (
        <>
          <MetaSep />
          {duration}
        </>
      )}
    </Timestamp>
  );
}
